import type { ButtonProps } from '@mui/material'
import { Button } from '@mui/material'
import type { FC, MouseEvent } from 'react'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import IcBaselineAdd from '~icons/ic/baseline-add'
import IcBaselineHowToReg from '~icons/ic/baseline-how-to-reg'
import IcBaselineReply from '~icons/ic/baseline-reply'
import IcBaselineSwapHoriz from '~icons/ic/baseline-swap-horiz'
import { useMyInfo } from 'src/services/apollo/auth'
import type { User } from 'src/services/apollo/generated/graphql'
import { executeSetFollowState } from 'src/services/apollo/user'
import {
  useUserFollowed,
  useUserFollowerCount,
  useUserFollowingMe,
} from 'src/stores/user-follow'
import { cx } from 'src/utils/cx'
import { getErrorData } from 'src/utils/errorUtil'
import { track } from 'src/utils/tracker'
import { openSignModal } from '../Account/utils'
import Text from '../ui/Text'

/**
 * Follow button states:
 * 1. not_following  — neither follows the other  → "+ Follow" (primary CTA)
 * 2. follow_back    — they follow me, I don't    → "Follow back" (primary CTA)
 * 3. following       — I follow them, they don't  → "✓ Following" (subtle/muted)
 * 4. mutual          — both follow each other     → "👥 Mutual" (subtle/distinct)
 */
export type FollowState =
  | 'not_following'
  | 'follow_back'
  | 'following'
  | 'mutual'

export interface UserFollowButtonProps extends ButtonProps {
  user?: User
  userId: string
  initialValue?: boolean
  initialFollowingMe?: boolean
  initialCount?: number
  /** Override styles/labels per state */
  stateOverrides?: Partial<
    Record<
      FollowState,
      {
        className?: string
        label?: string
        variant?: ButtonProps['variant']
        startIcon?: JSX.Element | null
      }
    >
  >
  /** @deprecated Use stateOverrides instead. Kept for backward compat. */
  followingProps?: {
    className?: string
    label?: string
    variant?: ButtonProps['variant']
    startIcon?: JSX.Element | null
  }
  /** @deprecated Use stateOverrides instead. Kept for backward compat. */
  followProps?: {
    className?: string
    label?: string
    variant?: ButtonProps['variant']
    startIcon?: JSX.Element | null
  }
  disableUnfollow?: boolean
  scene?: string
}

function getFollowState(followed: boolean, followingMe: boolean): FollowState {
  if (followed && followingMe) return 'mutual'
  if (followed) return 'following'
  if (followingMe) return 'follow_back'
  return 'not_following'
}

const UserFollowButton: FC<UserFollowButtonProps> = ({
  user,
  userId,
  initialValue = user?.followedByMe == null ? false : user?.followedByMe,
  initialFollowingMe = user?.followingMe == null ? false : user?.followingMe,
  initialCount = user?.followerCount || 0,
  className,
  stateOverrides,
  followProps,
  followingProps,
  disableUnfollow,
  scene,
  ...props
}) => {
  const { t } = useTranslation(['user'])
  const [followed, setFollowed] = useUserFollowed(userId, initialValue)
  const [followingMe] = useUserFollowingMe(userId, initialFollowingMe)
  const [count, setCount] = useUserFollowerCount(userId, initialCount)

  const { data: userInfo } = useMyInfo()

  const state = getFollowState(followed, followingMe)

  // Default config per state
  const defaults: Record<
    FollowState,
    {
      className: string
      label: string
      variant: ButtonProps['variant']
      startIcon: JSX.Element
    }
  > = {
    not_following: {
      // oxfmt-ignore
      className: 'bg-theme-primary text-white dark:text-white ring-1 ring-inset ring-black/20 dark:ring-white/30',
      label: t('user:follow'),
      variant: 'contained',
      startIcon: <IcBaselineAdd className="size-5" />,
    },
    follow_back: {
      // oxfmt-ignore
      className: 'bg-theme-primary text-white dark:text-white ring-1 ring-inset ring-black/20 dark:ring-white/30',
      label: t('user:follow-back'),
      variant: 'contained',
      startIcon: <IcBaselineReply className="size-5" />,
    },
    following: {
      className:
        'bg-transparent text-primary-light ring-1 ring-inset ring-primary-light/30',
      label: t('user:following'),
      variant: 'outlined',
      startIcon: <IcBaselineHowToReg className="size-5" />,
    },
    mutual: {
      className:
        'bg-transparent text-theme-primary ring-1 ring-inset ring-theme-primary/30',
      label: t('user:mutual-follow'),
      variant: 'outlined',
      startIcon: <IcBaselineSwapHoriz className="size-5" />,
    },
  }

  // Resolve config: stateOverrides > legacy props > defaults
  const resolved = (() => {
    const base = defaults[state]
    // Legacy prop compat: followProps applies to not_following & follow_back, followingProps to following & mutual
    const legacyOverride =
      state === 'not_following' || state === 'follow_back'
        ? followProps
        : followingProps
    const stateOverride = stateOverrides?.[state]
    return {
      className:
        stateOverride?.className ?? legacyOverride?.className ?? base.className,
      label: stateOverride?.label ?? legacyOverride?.label ?? base.label,
      variant:
        stateOverride?.variant ?? legacyOverride?.variant ?? base.variant,
      startIcon:
        stateOverride?.startIcon !== undefined
          ? stateOverride.startIcon
          : legacyOverride?.startIcon !== undefined
            ? legacyOverride.startIcon
            : base.startIcon,
    }
  })()

  const onClick = useCallback(
    async (e: MouseEvent) => {
      e.preventDefault()

      if (!userId) return
      if (!userInfo) {
        track('follow_btn_click', {
          notLogin: true,
          scene,
        })
        openSignModal(undefined, { fromPath: 'follow' })
        return
      }

      const target = !followed
      setFollowed(target)
      setCount(count + (target ? 1 : -1))

      try {
        track('follow_btn_click', {
          userId,
          target,
          scene,
        })
        await executeSetFollowState({
          userId,
          target,
        })
      } catch (err) {
        const errCode = getErrorData(err)?.code
        if (
          errCode === 40000013 // User blocked
        ) {
          toast.error(
            <>
              <Text i18nKey="common:block.denied-message" />
            </>,
          )
        } else {
          const msg = (err as Error).message ?? String(err)
          toast.error(msg)
        }

        setFollowed(followed)
        setCount(count)
      }
    },
    [userInfo, userId, followed, count],
  )

  if (userId === userInfo?.id) return null

  const button = (
    <Button
      onClick={onClick}
      variant={resolved.variant}
      className={cx(
        'rounded-md font-bold shadow-none',
        className,
        resolved.className,
      )}
      startIcon={resolved.startIcon}
      disabled={followed ? disableUnfollow : false}
      {...props}
    >
      <span className="capitalize">{resolved.label}</span>
    </Button>
  )

  return button
}

export default UserFollowButton
